import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calculator, CheckCircle2 } from 'lucide-react';
import { usePageSeo } from '../lib/seo';

const PAGE_URL = 'https://findmeagent.com.au/real-estate-agent-fees-australia';
const UPDATED = 'June 2026';

// Indicative ranges compiled from publicly reported industry averages. Rates are negotiable.
const STATE_RATES = [
  { state: 'New South Wales (NSW)', low: 1.8, high: 2.5, note: 'Sydney metro often 1.8-2.2%; regional NSW higher' },
  { state: 'Victoria (VIC)', low: 1.6, high: 2.5, note: 'Melbourne metro commonly 1.6-2.2%' },
  { state: 'Queensland (QLD)', low: 2.5, high: 3.0, note: 'Brisbane around 2.5%; regional QLD up to 3%+' },
  { state: 'Western Australia (WA)', low: 2.2, high: 3.0, note: 'Perth metro typically 2.2-2.8%' },
  { state: 'South Australia (SA)', low: 2.0, high: 2.75, note: 'Adelaide metro around 2-2.5%' },
  { state: 'Tasmania (TAS)', low: 2.5, high: 3.5, note: 'Lower prices push percentages higher' },
  { state: 'ACT', low: 2.0, high: 2.5, note: 'Canberra fairly consistent around 2.2%' },
  { state: 'Northern Territory (NT)', low: 2.5, high: 4.0, note: 'Darwin and regional rates vary widely' },
];

const EXTRA_COSTS = [
  ['Marketing and advertising', '$1,000 - $10,000+', 'Photography, floor plans, online listings, signboards, print. Usually paid upfront or at settlement.'],
  ['Auctioneer fee', '$400 - $1,000', 'Only if you sell by auction. Sometimes included in the agent\'s package.'],
  ['Conveyancing / solicitor', '$800 - $2,500', 'Not an agent fee, but part of your total selling costs.'],
  ['Styling and staging', '$2,000 - $8,000', 'Optional. Furniture hire for 4-6 weeks to present the home at its best.'],
  ['Pre-sale repairs and cleaning', 'Varies', 'Small fixes, gardens and a professional clean can lift the sale price.'],
];

const FEE_STRUCTURES = [
  ['Flat percentage', 'The most common model. A fixed percentage of the final sale price, e.g. 2%.'],
  ['Tiered (incentive) commission', 'A base rate up to an agreed price, then a higher rate on every dollar above it. Rewards the agent for beating expectations.'],
  ['Fixed fee', 'A set dollar amount regardless of sale price. More common with online or low-cost agencies.'],
  ['Marketing included vs extra', 'Some agents bundle marketing into the commission, most charge it separately. Always ask which.'],
];

const TIPS = [
  'Compare at least 2 to 3 agents. Rates and marketing packages differ more than most sellers expect.',
  'Ask for a tiered commission so the agent earns more only if your sale price exceeds the target.',
  'Negotiate the marketing budget line by line. Premium online listings matter; glossy brochures often do not.',
  'Judge on net result, not the cheapest rate. A strong agent who achieves 3% more on price easily covers a slightly higher fee.',
  'Check recent sales in your suburb. Local track record is a better predictor than brand size.',
  'Read the agency agreement before signing, including the exclusive period and any early-exit fees.',
];

const FAQS = [
  { q: 'What is the average real estate agent commission in Australia?', a: 'Most agents charge between 1.6% and 3% of the sale price, plus marketing costs. Metro areas with higher property prices tend to sit at the lower end, while regional and lower-priced markets are often higher.' },
  { q: 'Is the commission negotiable?', a: 'Yes. Commission is not regulated in most of Australia and agents expect to negotiate, especially when they know you are comparing several agents. Tiered structures are a common compromise.' },
  { q: 'When do I pay the agent?', a: 'Commission is normally deducted from the sale proceeds at settlement. Marketing costs are often paid upfront or when the property is listed, so ask about timing.' },
  { q: 'Does GST apply to agent fees?', a: 'Yes. Quoted rates should state whether they include GST. A 2% commission excluding GST is 2.2% including GST, so always compare like for like.' },
  { q: 'Are cheaper agents worse?', a: 'Not necessarily, but the fee is only one part of the equation. The best measure is the net amount in your pocket after fees, which depends heavily on the agent\'s negotiation skill and local knowledge.' },
  { q: 'How can FindMeAgent help with fees?', a: 'We introduce you to 2 to 3 pre-vetted local agents so you can compare their commission, marketing packages and recent results side by side. Our service is free for sellers.' },
];

const currency = (n) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

const FeeCalculator = () => {
  const [price, setPrice] = useState(850000);
  const [rate, setRate] = useState(2.2);
  const [marketing, setMarketing] = useState(3500);
  const fee = useMemo(() => (price * rate) / 100, [price, rate]);
  const total = fee + Number(marketing || 0);
  return (
    <div className="fee-calc" data-testid="fee-calculator">
      <div className="fee-calc-head">
        <Calculator size={22} aria-hidden="true" />
        <h3 className="fee-calc-title">Agent fee calculator</h3>
      </div>
      <div className="fee-calc-grid">
        <label className="fee-calc-field">
          <span>Expected sale price</span>
          <input type="number" min="0" step="10000" value={price} onChange={(e) => setPrice(Number(e.target.value))} data-testid="calc-price-input" />
        </label>
        <label className="fee-calc-field">
          <span>Commission rate (%)</span>
          <input type="number" min="0" max="10" step="0.1" value={rate} onChange={(e) => setRate(Number(e.target.value))} data-testid="calc-rate-input" />
        </label>
        <label className="fee-calc-field">
          <span>Marketing costs ($)</span>
          <input type="number" min="0" step="100" value={marketing} onChange={(e) => setMarketing(Number(e.target.value))} data-testid="calc-marketing-input" />
        </label>
      </div>
      <div className="fee-calc-results">
        <div><span>Commission</span><strong data-testid="calc-commission">{currency(fee)}</strong></div>
        <div><span>Marketing</span><strong>{currency(Number(marketing || 0))}</strong></div>
        <div className="fee-calc-total"><span>Estimated total selling cost</span><strong data-testid="calc-total">{currency(total)}</strong></div>
      </div>
      <p className="fee-calc-note">Estimates only, excluding GST unless your agent's quote includes it. Conveyancing and styling not included.</p>
    </div>
  );
};

export default function AgentFeesGuide() {
  usePageSeo({
    title: 'Real Estate Agent Fees & Commission Rates in Australia (2026 Guide) | FindMeAgent',
    description: 'How much do real estate agents charge in Australia? Average commission by state (1.6% to 3%+), marketing costs, fee structures, a fee calculator and negotiation tips.',
    canonical: PAGE_URL,
  });

  // FAQ + Article structured data for Google rich results
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'fees-guide-jsonld';
    script.text = JSON.stringify([
      {
        '@context': 'https://schema.org', '@type': 'Article',
        headline: 'Real Estate Agent Fees and Commission Rates in Australia',
        description: 'Average agent commission by state, marketing costs, fee structures and how to negotiate.',
        author: { '@type': 'Organization', name: 'FindMeAgent' },
        publisher: { '@type': 'Organization', name: 'FindMeAgent', logo: { '@type': 'ImageObject', url: 'https://findmeagent.com.au/logo512.png' } },
        mainEntityOfPage: PAGE_URL, dateModified: '2026-06-01',
      },
      {
        '@context': 'https://schema.org', '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
      },
    ]);
    document.head.appendChild(script);
    return () => script.remove();
  }, []);

  return (
    <div className="guide-page" data-testid="fees-guide-page">
      <header className="thankyou-topbar legal-topbar">
        <Link to="/" className="header-logo" data-testid="guide-logo-link">
          <div className="logo-icon"><span className="logo-text">FM</span></div>
          <span className="logo-name">FINDMEAGENT</span>
        </Link>
        <Link to="/" className="legal-back"><ArrowLeft size={16} /> Back to home</Link>
      </header>

      <main className="guide-main">
        <article className="guide-article">
          <p className="thankyou-eyebrow">Seller's guide · Updated {UPDATED}</p>
          <h1 className="guide-title" data-testid="guide-title">Real Estate Agent Fees &amp; Commission Rates in Australia</h1>
          <p className="guide-lead">
            Most Australian agents charge <strong>1.6% to 3% of the sale price</strong> plus marketing costs, but rates vary by state, suburb and
            how well you negotiate. This guide explains what you will pay, what it covers, and how to compare agents on value rather than
            just the cheapest rate.
          </p>

          <section className="guide-section" id="by-state">
            <h2 className="guide-heading">Average agent commission by state</h2>
            <p className="guide-text">Indicative ranges for residential sales. Percentages are of the final sale price and typically exclude GST and marketing.</p>
            <div className="guide-table-wrap">
              <table className="guide-table" data-testid="state-rates-table">
                <thead><tr><th>State / Territory</th><th>Typical range</th><th>Notes</th></tr></thead>
                <tbody>
                  {STATE_RATES.map((r) => (
                    <tr key={r.state}>
                      <td className="guide-td-strong">{r.state}</td>
                      <td className="guide-td-rate">{r.low.toFixed(1)}% - {r.high.toFixed(1)}%</td>
                      <td>{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="guide-text guide-small">Example: on a $850,000 sale, 2.2% commission is {currency(850000 * 0.022)} before GST and marketing.</p>
          </section>

          <section className="guide-section" id="calculator">
            <h2 className="guide-heading">Work out your likely selling costs</h2>
            <FeeCalculator />
          </section>

          <section className="guide-section" id="other-costs">
            <h2 className="guide-heading">Other costs to budget for</h2>
            <div className="guide-cards">
              {EXTRA_COSTS.map(([name, range, desc]) => (
                <div key={name} className="guide-card">
                  <h3 className="guide-card-title">{name}</h3>
                  <p className="guide-card-range">{range}</p>
                  <p className="guide-text">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="guide-section" id="structures">
            <h2 className="guide-heading">How agents structure their fees</h2>
            <ul className="guide-list">
              {FEE_STRUCTURES.map(([name, desc]) => (
                <li key={name}><strong>{name}.</strong> {desc}</li>
              ))}
            </ul>
          </section>

          <section className="guide-section" id="negotiate">
            <h2 className="guide-heading">6 ways to pay less without getting a worse agent</h2>
            <ul className="guide-checklist">
              {TIPS.map((t) => (
                <li key={t}><CheckCircle2 size={20} className="guide-check" aria-hidden="true" /><span>{t}</span></li>
              ))}
            </ul>
          </section>

          <section className="guide-cta" data-testid="guide-cta">
            <h2 className="guide-cta-title">Compare fees from pre-vetted agents in your suburb</h2>
            <p className="guide-cta-text">We shortlist 2 to 3 licence-checked local agents so you can compare commission, marketing and recent results side by side. Free for sellers, no obligation.</p>
            <Link to="/sell-your-property" className="hero-cta-primary guide-cta-button" data-testid="guide-cta-button">
              Get my free agent shortlist <ArrowRight size={18} />
            </Link>
          </section>

          <section className="guide-section" id="faq">
            <h2 className="guide-heading">Frequently asked questions</h2>
            <div className="guide-faqs">
              {FAQS.map((f) => (
                <details key={f.q} className="guide-faq" data-testid="guide-faq-item">
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          <p className="guide-disclaimer">
            Figures are indicative industry ranges compiled from publicly reported data and are provided as general information only, not financial or legal advice. Always confirm fees in writing with your agent.
          </p>
        </article>
      </main>
    </div>
  );
}
